<template>
  <editable-base
    ref="base"
    :mode="innerMode"
    :no-label="true"
    :error-message="errorMessage"
    :readonly="readonly"
    :show-buttons="showButtons"
    :showErrorMessage="showErrorMessage"
  >
    <b-input-group v-if="mode === modes.edit || mode === modes.processing">
      <b-form-input
        :class="[
          'est-input',
          mode === modes.processing ? 'text-secondary' : '',
          readonly ? 'readonly' : ''
        ]"
        :id="id"
        v-model="editValue.number"
        :plaintext="mode !== modes.edit && mode !== modes.processing"
        :readonly="readonly || mode !== modes.edit"
        v-mask="'(###) ###-####'"
        :state="phoneNumberValidation.getState(formState.validate)"
      />
      <b-form-select
        v-model="editValue.type"
        :options="phoneNumberOptions"
        :disabled="readonly || mode !== modes.edit"
      ></b-form-select>
      <b-input-group-append is-text>
        <b-form-checkbox
          v-model="editValue.isPreferredContactNumber"
          :disabled="readonly || mode !== modes.edit"
        >
          Preferred Contact Number</b-form-checkbox
        >
      </b-input-group-append>
    </b-input-group>
    <div v-else @click="handleMaybeEditClick">
      <span class="text-number">{{
        editValue.number | VMask("(###) ###-####")
      }}</span>
      <span class="text-type text-muted ml-3">{{ editValue.type }}</span>
      <span
        v-if="editValue.isPreferredContactNumber"
        class="text-preferred small text-muted ml-3"
      >
        preferred contact</span
      >
    </div>
    <b-button
      v-if="isDeletable && mode !== modes.edit && mode !== modes.processing"
      class="delete-button red ml-3"
      variant="primary"
      size="sm"
      :disabled="mode !== modes.default"
      @click="emitDeleteRequest"
      ><b-icon-x></b-icon-x> delete</b-button
    >
  </editable-base>
</template>

<script>
import {
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BInputGroupAppend,
  BInputGroup,
  BIconX,
  BButton
} from "bootstrap-vue";
import EditableBase, { Mixin } from "./EditableBase";
import { EmitUp } from "@/components/lib/VueHelpers";
import { IsValidPhoneNumber } from "../lib/Validation";
import { VueMaskDirective } from "v-mask";
import { VueMaskFilter } from "v-mask";

export const CancelNewPhoneNumberFormEvent = "cancel-new-phone-number-form";
export default {
  name: "EditablePhoneNumber",
  mixins: [Mixin],
  directives: { mask: VueMaskDirective },
  filters: { VMask: VueMaskFilter },
  props: {
    phoneNumberTypes: Array,
    initialValue: Object,
    name: Number, // uses the id value (an int) for the name
    isDeletable: { type: Boolean, default: true },
    isNewPhoneForm: { type: Boolean, default: false }
  },
  methods: {
    /**
     * Since the value for this object is complex (an object instead of a string, number, or boolean), the setValue
     * method must be overridden to avoid value and editValue having the same object reference.
     * @param {object} newValue
     */
    setValue(newValue) {
      this.value = {
        number: newValue.number,
        isPreferredContactNumber: newValue.isPreferredContactNumber,
        type: newValue.type
      };
      this.editValue = {
        number: newValue.number,
        isPreferredContactNumber: newValue.isPreferredContactNumber,
        type: newValue.type
      };
    },
    /**
     * Updates the display value based on the response
     *
     * Unlike other profile fields, phone numbers use an entirely different URL to update.
     *
     * Instead of newValue, there is a phoneNumber field in the response.
     * @param {object} response
     **/
    saveSuccessHandler(response) {
      this.setValue(response.data.phoneNumber);
      this.mode = this.modes.default;
    },
    /**
     * Convert the complex object into just the phone number string
     * @param {object} value
     */
    preValidateValueProcessor(value) {
      return value.number;
    },
    emitDeleteRequest() {
      this.mode = this.modes.processing;
      let that = this;
      EmitUp(
        this.events.delete,
        {
          id: this.editValue.id,
          failureCallback: () => {
            that.mode = that.modes.default;
            that.innerMode = that.modes.default;
          }
        },
        this
      );
    },
    /**
     * Override the default handling of cancel
     **/
    handleCancelEdit() {
      if (this.isNewPhoneForm) {
        this.$emit(CancelNewPhoneNumberFormEvent, this);
      } else {
        // call original
        Mixin.methods.handleCancelEdit.bind(this)();
      }
    },
    /**
     * Override the default handling when isDirty returns false during a save attempt
     **/
    cleanupCanceledSaveAttempt() {
      if (this.isNewPhoneForm) {
        this.errorMessage = "cannot save until the form has been modified";
      } else {
        // call original
        Mixin.methods.cleanupCanceledSaveAttempt.bind(this)();
      }
    },
    /**
     * Override of default mixing method
     *
     * The default expects a string value, and doesn't properly pick up on changed object properties.
     * @returns {boolean}
     */
    isDirty() {
      let value =
        this.editValue.number !== this.value.number ||
        this.editValue.isPreferredContactNumber !==
          this.value.isPreferredContactNumber ||
        this.editValue.type !== this.value.type;
      return value;
    },
    /**
     * Override the default pre-save transformation
     *
     * By default, the value is coerced into a string. This override keeps the object structure but coerces the boolean
     * into a string.
     *
     * It also only includes properties that have changed.
     * @param value
     * @returns {*}
     */
    preSaveValueProcessor(value) {
      if (this.hasValidationErrors) {
        return;
      }
      // don't modify the value for a new phone form -- keep all the data types the same
      if (this.isNewPhoneForm) {
        return value;
      }
      let temp = {};
      if (value.number !== this.value.number) {
        temp.number = value.number;
      }
      if (
        value.isPreferredContactNumber !== this.value.isPreferredContactNumber
      ) {
        // transform bool to string --- temporary to compensate for the (incomplete) auto-updatable code serverside
        temp.isPreferredContactNumber = value.isPreferredContactNumber
          ? "true"
          : "false";
      }
      if (value.type !== this.value.type) {
        temp.type = value.type;
      }
      return temp;
    }
  },
  computed: {
    innerMode() {
      return this.mode;
    },
    formState() {
      return {
        validate: this.errorMessage !== null
      };
    },
    showErrorMessage() {
      return this.hasValidationErrors;
    },
    phoneNumberValidation() {
      return IsValidPhoneNumber(this.editValue.number);
    },
    hasValidationErrors() {
      // each field on the form has to be listed here in order for it to work
      let checklist = [this.phoneNumberValidation];
      return checklist.some(x => true !== x.isValid);
    }
  },
  watch: {
    /**
     * Watches changes to the initialValue property
     *
     * When it changes, the editValue and value are reset.
     *
     * TODO see if it makes sense to modify how savers work, so that they always update initialValue, and get rid of value entirely -- always compare editValue and initialValue (if this is done, perhaps initialValue is the wrong name -- perhaps it should be referenceValue)
     * @param newValue
     */
    "initialValue.isPreferredContactNumber"(newValue) {
      this.editValue.isPreferredContactNumber = newValue;
      this.value.isPreferredContactNumber = newValue;
    }
  },
  data() {
    return {
      // it is necessary to have a new object reference for value and editValue --
      // instead of deep copying, since the structure is small and well-known, handled
      // by hand. If it grows in complexity or seems to change often, consider including
      // a function e.g. from lodash or similar to perform a deep copy.
      value: {
        number: this.initialValue.number,
        type: this.initialValue.type,
        isPreferredContactNumber: this.initialValue.isPreferredContactNumber
      },
      editValue: {
        number: this.initialValue.number,
        type: this.initialValue.type,
        isPreferredContactNumber: this.initialValue.isPreferredContactNumber
      },
      phoneNumberOptions: this.phoneNumberTypes.map(x => {
        return { value: x, text: x };
      })
    };
  },
  components: {
    BFormInput,
    BFormSelect,
    EditableBase,
    BFormCheckbox,
    BInputGroupAppend,
    BInputGroup,
    BIconX,
    BButton
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss"></style>
